import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  filterCampaignsByDateRange,
  getActiveFlightsPairedWithCampaigns,
  getPastFlightsPairedWithCampaigns,
  isCampaignActive,
} from 'src/components/SponsoredListings';
import {
  AdCampaignsDataContextProvider,
  useAdCampaignsDataContext,
} from 'src/contexts/AdCampaignsDataContext/AdCampaignsDataContext';
import { CatalogDataContextProvider } from 'src/contexts/CatalogDataContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Stack } from 'src/core/ui';
import { LayoutContent } from 'src/navigations/LayoutContent';
import {
  CampaignFlightsTable,
  CampaignsTableV2,
} from 'src/tables/SponsoredListings';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getLastDateRange,
  StandardDateRangePresetNames,
} from 'src/utils/dateTimeUtils';
import {
  DefaultListingQuery,
  EmptyListingQuery,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { ListingCatalogQueryKey } from 'src/utils/inventoryUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  DateTimeRange,
  ListingQuery,
  UserSetting,
} from 'src/WebApiController';

import { MainRoute } from '../MainRoute';
import * as styles from './SponsoredListings.css';
import { useSponsoredListingUrl } from './SponsoredListings.hook';
import { SponsoredListingsSectionType } from './SponsoredListings.types';
import { SwitchToButton } from './SwitchToButton/SwitchToButton';

export function SponsoredListings() {
  return (
    <FilterQueryContextProvider<ListingQuery>
      initialQuery={
        {
          ...DefaultListingQuery,
          eventDates: {
            start: getLastDateRange(
              { days: 30 },
              new Date()
            ).start.toISOString(),
            end: null,
          } as DateTimeRange,
        } as ListingQuery
      }
      emptyQuery={EmptyListingQuery}
      viewModeSettingId={UserSetting.InventoryPageViewMode}
      saveQueryInUrl
    >
      <CatalogDataContextProvider<ListingQuery>
        entityType={ActionOutboxEntityType.Listing}
        queryKey={ListingCatalogQueryKey}
        transformEventData={transformData}
      >
        <AdCampaignsDataContextProvider loadSalesDetails>
          <SponsoredListingsContent />
        </AdCampaignsDataContextProvider>
      </CatalogDataContextProvider>
    </FilterQueryContextProvider>
  );
}

function SponsoredListingsContent() {
  const sponsoredListingsTitle = useContent(ContentId.SponsoredListings);

  const { initialSectionType, initialDateRangeFilter, updateUrlParams } =
    useSponsoredListingUrl();

  const [isLoading, setIsLoading] = useState(() => false);
  const [sectionType, setSectionType] =
    useState<SponsoredListingsSectionType>(initialSectionType);

  const [dateRangeFilter, setDateRangeFilter] = useState<DateTimeRange | null>(
    initialDateRangeFilter
  );

  // Update URL when state changes
  useEffect(() => {
    updateUrlParams(sectionType, dateRangeFilter);
  }, [sectionType, dateRangeFilter, updateUrlParams]);

  const handleSectionTypeChange = useCallback(
    (newSectionType: SponsoredListingsSectionType) => {
      setSectionType(newSectionType);
    },
    []
  );

  const handleDateRangeFilterChange = useCallback(
    (newDateRangeFilter: DateTimeRange | null) => {
      setDateRangeFilter(newDateRangeFilter);
    },
    []
  );

  const {
    allCampaignData,
    adCampaignsDataQuery: adCampaignsDataQuery,
    errorInfo,
    refreshData,
  } = useAdCampaignsDataContext();

  const allCampaigns = useMemo(() => {
    if (!allCampaignData) {
      return null;
    }
    return filterCampaignsByDateRange(
      Object.values(allCampaignData).flat(),
      dateRangeFilter
    );
  }, [allCampaignData, dateRangeFilter]);

  const { activeCampaigns, inactiveCampaigns } = useMemo(() => {
    if (allCampaigns == null) {
      return { activeCampaigns: null, inactiveCampaigns: null };
    }
    const activeCampaigns = allCampaigns.filter((c) => isCampaignActive(c));

    const inactiveCampaigns = allCampaigns.filter((c) => !isCampaignActive(c));
    return { activeCampaigns, inactiveCampaigns };
  }, [allCampaigns]);

  const campaignsWithActiveFlights = useMemo(
    () => getActiveFlightsPairedWithCampaigns(allCampaigns),
    [allCampaigns]
  );

  const campaignsWithPastFlights = useMemo(
    () => getPastFlightsPairedWithCampaigns(allCampaigns),
    [allCampaigns]
  );

  return (
    <DialogProvider>
      <LayoutContent
        mainRoute={MainRoute.SponsoredListings}
        routeTitle={sponsoredListingsTitle}
      >
        <Stack
          direction="column"
          width="full"
          height="full"
          className={styles.tableSection}
        >
          {adCampaignsDataQuery.isLoading && <PosSpinner />}
          {errorInfo && (
            <SomethingWentWrong
              header={errorInfo.errorHeader}
              message={
                errorInfo.errorMessage ?? (
                  <Content id={ContentId.FailToLoadListContent} />
                )
              }
            />
          )}
          {!adCampaignsDataQuery.isLoading && !errorInfo && (
            <>
              <Stack
                gap="l"
                direction="column"
                width="full"
                style={{
                  height: activeCampaigns?.length ? '100%' : 'fit-content',
                }}
              >
                <Stack
                  gap="l"
                  justifyContent="spaceBetween"
                  alignItems="center"
                >
                  <span>
                    <Content
                      id={
                        sectionType ===
                        SponsoredListingsSectionType.CampaignsView
                          ? ContentId.ActiveCampaigns
                          : ContentId.ActiveFlights
                      }
                    />{' '}
                    (
                    {sectionType === SponsoredListingsSectionType.CampaignsView
                      ? activeCampaigns?.length
                      : (campaignsWithActiveFlights?.length ?? 0)}
                    )
                  </span>
                  <Stack gap="l" direction="row" alignItems="center">
                    <SwitchToButton
                      sectionType={sectionType}
                      onSwitchSection={handleSectionTypeChange}
                    />
                    <DateRangeSelector
                      presetNames={StandardDateRangePresetNames}
                      value={dateRangeFilter}
                      onChange={({ value }) =>
                        handleDateRangeFilterChange(value ?? null)
                      }
                    />
                  </Stack>
                </Stack>
                {sectionType === SponsoredListingsSectionType.CampaignsView ? (
                  <CampaignsTableV2
                    showEventColumns
                    disabled={isLoading}
                    isLoading={adCampaignsDataQuery.isLoading}
                    campaigns={activeCampaigns}
                    onStartWork={() => setIsLoading(true)}
                    onStopWork={(success) => {
                      setIsLoading(false);
                      if (success) refreshData();
                    }}
                  />
                ) : (
                  <CampaignFlightsTable
                    disabled={isLoading}
                    isLoading={adCampaignsDataQuery.isLoading}
                    campaignsAndFlights={campaignsWithActiveFlights}
                    onStartWork={() => setIsLoading(true)}
                    onStopWork={(success) => {
                      setIsLoading(false);
                      if (success) refreshData();
                    }}
                  />
                )}
              </Stack>
              <Stack
                gap="l"
                direction="column"
                width="full"
                style={{
                  height: inactiveCampaigns?.length ? '100%' : 'fit-content',
                }}
              >
                <span>
                  <Content
                    id={
                      sectionType === SponsoredListingsSectionType.CampaignsView
                        ? ContentId.InactiveCampaigns
                        : ContentId.HistoricPerformance
                    }
                  />{' '}
                  (
                  {sectionType === SponsoredListingsSectionType.CampaignsView
                    ? inactiveCampaigns?.length
                    : (campaignsWithPastFlights?.length ?? 0)}
                  )
                </span>
                {sectionType === SponsoredListingsSectionType.CampaignsView ? (
                  <CampaignsTableV2
                    showEventColumns
                    disabled={isLoading}
                    isLoading={adCampaignsDataQuery.isLoading}
                    campaigns={inactiveCampaigns}
                    onStartWork={() => setIsLoading(true)}
                    onStopWork={(success) => {
                      setIsLoading(false);
                      if (success) refreshData();
                    }}
                  />
                ) : (
                  <CampaignFlightsTable
                    disabled={isLoading}
                    isLoading={adCampaignsDataQuery.isLoading}
                    campaignsAndFlights={campaignsWithPastFlights}
                    groupByDate
                    onStartWork={() => setIsLoading(true)}
                    onStopWork={(success) => {
                      setIsLoading(false);
                      if (success) refreshData();
                    }}
                  />
                )}
              </Stack>
            </>
          )}
        </Stack>
      </LayoutContent>
    </DialogProvider>
  );
}
